import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../services/axiosInstance';
import { useBirds } from '../hooks/useBirds';

const COLORS = { a: '#42a5f5', b: '#ff7043' };

type Granularity = 'week' | 'day';

function useBirdComparison(birdA: number | null, birdB: number | null, granularity: Granularity) {
  return useQuery({
    queryKey: ['egg-compare', birdA, birdB, granularity],
    queryFn: async () => {
      const res = await axiosInstance.get('/egg-productions/compare', {
        params: { bird_a: birdA, bird_b: birdB, granularity },
      });
      return res.data.data as {
        bird_a: ComparisonSide;
        bird_b: ComparisonSide;
      };
    },
    enabled: !!birdA && !!birdB,
  });
}

interface DataPoint {
  age_period: number;
  total_eggs: number;
  crate_count: number;
  avg_lay_pct: number;
  production_days: number;
}

interface ComparisonSide {
  id: number;
  name: string;
  color: string;
  birds_type: string;
  date_delivered: string | null;
  granularity: Granularity;
  series: DataPoint[];
}

type Metric = 'avg_lay_pct' | 'total_eggs' | 'crate_count';

const METRIC_LABELS: Record<Metric, string> = {
  avg_lay_pct: 'Lay % (avg)',
  total_eggs: 'Total Eggs',
  crate_count: 'Crates',
};

function buildChartData(
  a: ComparisonSide | undefined,
  b: ComparisonSide | undefined,
  from: number,
  to: number
): Array<Record<string, number | null>> {
  if (!a || !b) return [];

  const norm = (p: DataPoint) => ({
    age_period: Number(p.age_period),
    avg_lay_pct: Number(p.avg_lay_pct),
    total_eggs: Number(p.total_eggs),
    crate_count: Number(p.crate_count),
    production_days: Number(p.production_days),
  });

  const periods = new Set<number>();
  a.series.forEach((p) => periods.add(Number(p.age_period)));
  b.series.forEach((p) => periods.add(Number(p.age_period)));

  const aMap = new Map(a.series.map((p) => [Number(p.age_period), norm(p)]));
  const bMap = new Map(b.series.map((p) => [Number(p.age_period), norm(p)]));

  return Array.from(periods)
    .sort((x, y) => x - y)
    .filter((v) => v >= from && v <= to)
    .map((period) => ({
      period,
      a_lay: aMap.get(period)?.avg_lay_pct ?? null,
      b_lay: bMap.get(period)?.avg_lay_pct ?? null,
      a_eggs: aMap.get(period)?.total_eggs ?? null,
      b_eggs: bMap.get(period)?.total_eggs ?? null,
      a_crates: aMap.get(period)?.crate_count ?? null,
      b_crates: bMap.get(period)?.crate_count ?? null,
    }));
}

function StatChip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h6" fontWeight={800} sx={{ color }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function SummaryCard({
  side,
  color,
  label,
  granularity,
}: {
  side: ComparisonSide;
  color: string;
  label: string;
  granularity: Granularity;
}) {
  const s = side.series.map((p) => ({
    ...p,
    avg_lay_pct: Number(p.avg_lay_pct),
    total_eggs: Number(p.total_eggs),
    crate_count: Number(p.crate_count),
    age_period: Number(p.age_period),
  }));
  const avgLay = s.length
    ? (s.reduce((acc, p) => acc + p.avg_lay_pct, 0) / s.length).toFixed(1)
    : '—';
  const peakLay = s.length ? Math.max(...s.map((p) => p.avg_lay_pct)).toFixed(1) : '—';
  const peakPeriod = s.length
    ? s.reduce((best, p) => (p.avg_lay_pct > best.avg_lay_pct ? p : best), s[0]).age_period
    : '—';
  const totalCrates = s.reduce((acc, p) => acc + p.crate_count, 0).toFixed(0);
  const periodCount = s.length;
  const peakLabel = granularity === 'day' ? `Day ${peakPeriod}` : `W${peakPeriod}`;

  return (
    <Card variant="outlined" sx={{ flex: 1, borderColor: color, borderWidth: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="subtitle1" fontWeight={800} gutterBottom noWrap>
          {side.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {side.birds_type}
          {side.date_delivered ? ` · Delivered ${side.date_delivered}` : ''}
        </Typography>
        <Stack direction="row" justifyContent="space-around">
          <StatChip label="Avg Lay %" value={`${avgLay}%`} color={color} />
          <StatChip label="Peak Lay %" value={`${peakLay}%`} color={color} />
          <StatChip
            label={granularity === 'day' ? 'Peak Day' : 'Peak Week'}
            value={peakLabel}
            color={color}
          />
          <StatChip label="Total Crates" value={totalCrates} color={color} />
          <StatChip
            label={granularity === 'day' ? 'Days' : 'Weeks'}
            value={`${periodCount}`}
            color={color}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function EggProductionComparison() {
  const [birdA, setBirdA] = useState<number | ''>('');
  const [birdB, setBirdB] = useState<number | ''>('');
  const [metric, setMetric] = useState<Metric>('avg_lay_pct');
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [rangeFrom, setRangeFrom] = useState<string>('');
  const [rangeTo, setRangeTo] = useState<string>('');
  const [highlight, setHighlight] = useState<string>('');

  const { data: birdsData } = useBirds({ per_page: 200 });
  const birds = (birdsData as any)?.data ?? [];

  const { data, isFetching, error } = useBirdComparison(birdA || null, birdB || null, granularity);

  const from = rangeFrom !== '' ? parseInt(rangeFrom) : 0;
  const to = rangeTo !== '' ? parseInt(rangeTo) : 99999;

  const chartData = buildChartData(data?.bird_a, data?.bird_b, from, to);

  const aKey = metric === 'avg_lay_pct' ? 'a_lay' : metric === 'total_eggs' ? 'a_eggs' : 'a_crates';
  const bKey = metric === 'avg_lay_pct' ? 'b_lay' : metric === 'total_eggs' ? 'b_eggs' : 'b_crates';
  const aLabel = data ? data.bird_a.name : 'Bird A';
  const bLabel = data ? data.bird_b.name : 'Bird B';
  const yLabel = metric === 'avg_lay_pct' ? 'Lay %' : metric === 'total_eggs' ? 'Eggs' : 'Crates';

  const xLabel = granularity === 'day' ? 'Age (days)' : 'Age (weeks)';
  const tickFmt = (v: number) => (granularity === 'day' ? `D${v}` : `W${v}`);
  const tooltipLabel = (v: number) => (granularity === 'day' ? `Day ${v}` : `Week ${v}`);
  const fromLabel = granularity === 'day' ? 'Day from' : 'Week from';
  const toLabel = granularity === 'day' ? 'Day to' : 'Week to';
  const highlightLabel = granularity === 'day' ? 'Highlight day' : 'Highlight week';
  const highlightPlaceholder = granularity === 'day' ? 'e.g. 126' : 'e.g. 18';
  const unitLabel = granularity === 'day' ? 'Day' : 'Week';

  const handleGranularityChange = (_: React.MouseEvent<HTMLElement>, v: Granularity | null) => {
    if (!v) return;
    setGranularity(v);
    setRangeFrom('');
    setRangeTo('');
    setHighlight('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Laying Comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Compare two flocks&apos; egg production side-by-side, normalised by age.
      </Typography>

      {/* Bird selectors */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Flock A</InputLabel>
              <Select
                label="Flock A"
                value={birdA}
                onChange={(e) => setBirdA(e.target.value as number)}
              >
                {birds.map((b: any) => (
                  <MenuItem key={b.id} value={b.id} disabled={b.id === birdB}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <SwapHorizIcon color="disabled" />

            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Flock B</InputLabel>
              <Select
                label="Flock B"
                value={birdB}
                onChange={(e) => setBirdB(e.target.value as number)}
              >
                {birds.map((b: any) => (
                  <MenuItem key={b.id} value={b.id} disabled={b.id === birdA}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {isFetching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load comparison data.
        </Alert>
      )}

      {data && !isFetching && (
        <>
          {/* Summary cards */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
            <SummaryCard
              side={data.bird_a}
              color={COLORS.a}
              label="Flock A"
              granularity={granularity}
            />
            <SummaryCard
              side={data.bird_b}
              color={COLORS.b}
              label="Flock B"
              granularity={granularity}
            />
          </Stack>

          {/* Chart controls */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
                flexWrap="wrap"
              >
                {/* Granularity toggle */}
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={granularity}
                  onChange={handleGranularityChange}
                >
                  <ToggleButton value="week">By Week</ToggleButton>
                  <ToggleButton value="day">By Day</ToggleButton>
                </ToggleButtonGroup>

                {/* Metric toggle */}
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={metric}
                  onChange={(_, v) => v && setMetric(v)}
                >
                  {(Object.entries(METRIC_LABELS) as [Metric, string][]).map(([k, label]) => (
                    <ToggleButton key={k} value={k}>
                      {label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <TextField
                  label={fromLabel}
                  type="number"
                  size="small"
                  value={rangeFrom}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  sx={{ width: 120 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <TextField
                  label={toLabel}
                  type="number"
                  size="small"
                  value={rangeTo}
                  onChange={(e) => setRangeTo(e.target.value)}
                  sx={{ width: 120 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <TextField
                  label={highlightLabel}
                  type="number"
                  size="small"
                  value={highlight}
                  onChange={(e) => setHighlight(e.target.value)}
                  sx={{ width: 150 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                  placeholder={highlightPlaceholder}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Chart */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} mb={2}>
                {METRIC_LABELS[metric]} by {xLabel}
              </Typography>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={chartData} margin={{ top: 4, right: 24, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff18" />
                  <XAxis
                    dataKey="period"
                    tickFormatter={tickFmt}
                    label={{
                      value: xLabel,
                      position: 'insideBottom',
                      offset: -2,
                      fontSize: 12,
                    }}
                  />
                  <YAxis
                    tickFormatter={(v) => (metric === 'avg_lay_pct' ? `${v}%` : v.toLocaleString())}
                    label={{ value: yLabel, angle: -90, position: 'insideLeft', fontSize: 12 }}
                    width={55}
                  />
                  <Tooltip
                    formatter={(value: any, name: string | undefined) => [
                      metric === 'avg_lay_pct' ? `${value}%` : value,
                      name ?? '',
                    ]}
                    labelFormatter={(label) => tooltipLabel(label)}
                  />
                  <Legend />
                  {highlight !== '' && (
                    <ReferenceLine
                      x={parseInt(highlight)}
                      stroke="#ffeb3b"
                      strokeDasharray="4 2"
                      label={{
                        value: tickFmt(parseInt(highlight)),
                        fill: '#ffeb3b',
                        fontSize: 11,
                      }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey={aKey}
                    name={aLabel}
                    stroke={COLORS.a}
                    dot={false}
                    strokeWidth={2}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey={bKey}
                    name={bLabel}
                    stroke={COLORS.b}
                    dot={false}
                    strokeWidth={2}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Data table */}
          <Card variant="outlined">
            <CardContent sx={{ pb: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                {unitLabel} Detail
              </Typography>
            </CardContent>
            <TableContainer sx={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{unitLabel}</TableCell>
                    <TableCell align="right" sx={{ color: COLORS.a }}>
                      {data.bird_a.name} — Lay %
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLORS.b }}>
                      {data.bird_b.name} — Lay %
                    </TableCell>
                    <TableCell align="right">Δ Lay %</TableCell>
                    <TableCell align="right" sx={{ color: COLORS.a }}>
                      A Eggs
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLORS.b }}>
                      B Eggs
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((row) => {
                    const aLay = row.a_lay as number | null;
                    const bLay = row.b_lay as number | null;
                    const delta = aLay != null && bLay != null ? +(aLay - bLay).toFixed(1) : null;
                    const isHighlight = highlight !== '' && row.period === parseInt(highlight);
                    return (
                      <TableRow
                        key={row.period}
                        sx={isHighlight ? { bgcolor: '#ffeb3b22' } : undefined}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2" fontWeight={isHighlight ? 700 : 400}>
                              {tickFmt(row.period as number)}
                            </Typography>
                            {isHighlight && (
                              <Chip
                                label="highlighted"
                                size="small"
                                sx={{ height: 16, fontSize: 10 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ color: COLORS.a }}>
                          {aLay != null ? `${aLay}%` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: COLORS.b }}>
                          {bLay != null ? `${bLay}%` : '—'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              delta == null
                                ? 'text.secondary'
                                : delta > 0
                                  ? COLORS.a
                                  : delta < 0
                                    ? COLORS.b
                                    : 'text.primary',
                            fontWeight: 600,
                          }}
                        >
                          {delta != null ? (delta > 0 ? `+${delta}%` : `${delta}%`) : '—'}
                        </TableCell>
                        <TableCell align="right">{row.a_eggs?.toLocaleString() ?? '—'}</TableCell>
                        <TableCell align="right">{row.b_eggs?.toLocaleString() ?? '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {!birdA && !birdB && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            Select two flocks above to start comparing.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
